// Package Imports
import type { ReactNode } from 'react'
import { HeadContent, Outlet } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, theme as antdTheme } from 'antd'
import trTR from 'antd/locale/tr_TR'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { useTheme, useThemeSync } from '@/core/hooks/use-theme'

// Shared Imports
import AuthEventsBinder from '@/core/components/auth-events-binder'
import GlobalMutateLoader from '@/core/components/global-mutate-loader'

const ThemedAntd = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme()
  useThemeSync()
  const isCanli = theme === 'canli'

  return (
    <ConfigProvider
      locale={trTR}
      theme={{
        algorithm: isCanli
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        token: isCanli
          ? {
              colorPrimary: '#7c5cff',
              colorInfo: '#22d3ee',
              colorSuccess: '#34d399',
              colorWarning: '#fbbf24',
              colorError: '#f43f5e',
              colorBgBase: '#1a1340',
              colorBgContainer: '#241a55',
              colorBgElevated: '#2d2168',
              colorBorder: '#3a2d7a',
              colorText: '#f5f3ff',
              colorTextSecondary: '#c4b5fd',
              borderRadius: 14,
            }
          : {
              // Darkened from #7275f2 → 5.04:1 with white text (WCAG AA).
              colorPrimary: '#4f46e5',
              // Antd default #8c8c8c fails AA on white (3.36). Bumped to gray-7.
              colorTextSecondary: '#595959',
              colorTextDescription: '#595959',
              borderRadius: 8,
            },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

const Root = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemedAntd>
        <AuthEventsBinder />
        <GlobalMutateLoader />
        <HeadContent />
        <Outlet />
      </ThemedAntd>
    </QueryClientProvider>
  )
}

export default Root
